import { api } from "@/lib/api";

export interface GithubInstallation {
	installation_id: number;
	account_login: string | null;
	account_type: string | null;
}

export interface GithubAppInfo {
	app_id: number;
	slug: string | null;
	html_url: string | null;
	webhook_configured: boolean;
}

export interface GithubStatus {
	configured: boolean;
	connected: boolean;
	installations?: GithubInstallation[];
	app?: GithubAppInfo | null;
}

export interface GithubRepo {
	owner: string;
	name: string;
	full_name: string;
	default_branch: string;
	private: boolean;
	installation_id: number;
}

export interface ProjectRepo {
	project_id: string;
	installation_id: number;
	repo_owner: string;
	repo_name: string;
	default_branch: string;
}

export async function getGithubStatus(): Promise<GithubStatus> {
	const res = await api.get<{ success: boolean; data: GithubStatus }>(
		`/integrations/github/status`,
	);
	return res.data;
}

// Fetches the GitHub App manifest for this system and POSTs it to GitHub so the
// new-App form arrives pre-filled. Navigates away on submit.
export async function startGithubAppCreation(): Promise<void> {
	const res = await api.get<{
		success: boolean;
		data: { manifest: Record<string, unknown>; action_url: string };
	}>(`/integrations/github/manifest`);

	const form = document.createElement("form");
	form.method = "POST";
	form.action = res.data.action_url;
	const input = document.createElement("input");
	input.type = "hidden";
	input.name = "manifest";
	input.value = JSON.stringify(res.data.manifest);
	form.appendChild(input);
	document.body.appendChild(form);
	form.submit();
}

// Returns the install URL the browser should navigate to.
export async function connectGithub(): Promise<string> {
	const res = await api.post<{ success: boolean; data: { url: string } }>(
		`/integrations/github/connect`,
		{},
	);
	return res.data.url;
}

// Registers an installation id that GitHub handed to the frontend (Apps whose
// setup URL pointed at the client). The server verifies it against GitHub.
export async function registerGithubInstallation(
	installationId: number,
): Promise<GithubInstallation> {
	const res = await api.post<{ success: boolean; data: GithubInstallation }>(
		`/integrations/github/installations`,
		{ installation_id: installationId },
	);
	return res.data;
}

// Removes a stored installation (and, via DB cascade, any project repo
// mappings on that account). Does not touch the GitHub App itself.
export async function disconnectGithubInstallation(
	installationId: number,
): Promise<void> {
	await api.delete(`/integrations/github/installations/${installationId}`);
}

// Full reset: deletes the stored App config and all installations, so
// "Create GitHub App" starts fresh. Does not delete the App on GitHub itself.
export async function removeGithubApp(): Promise<void> {
	await api.delete(`/integrations/github/app`);
}

export interface ProjectRepoWithProject extends ProjectRepo {
	project: { id: string; name: string } | null;
}

// All project -> repo mappings, for the workspace settings card.
export async function listProjectRepos(): Promise<ProjectRepoWithProject[]> {
	const res = await api.get<{
		success: boolean;
		data: ProjectRepoWithProject[];
	}>(`/integrations/github/projects/repos`);
	return res.data;
}

export async function listGithubRepos(): Promise<GithubRepo[]> {
	const res = await api.get<{ success: boolean; data: GithubRepo[] }>(
		`/integrations/github/repos`,
	);
	return res.data;
}

export async function getProjectRepo(
	projectId: string,
): Promise<ProjectRepo | null> {
	const res = await api.get<{ success: boolean; data: ProjectRepo | null }>(
		`/integrations/github/projects/${projectId}/repo`,
	);
	return res.data;
}

export async function setProjectRepo(
	projectId: string,
	payload: {
		installation_id: number;
		repo_owner: string;
		repo_name: string;
		default_branch: string;
	},
): Promise<ProjectRepo> {
	const res = await api.put<{ success: boolean; data: ProjectRepo }>(
		`/integrations/github/projects/${projectId}/repo`,
		payload,
	);
	return res.data;
}

export async function unsetProjectRepo(projectId: string): Promise<void> {
	await api.delete(`/integrations/github/projects/${projectId}/repo`);
}
